import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { StorageLocation } from '@knust/api-interfaces';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { debounceTime, distinctUntilChanged, pipe, switchMap, tap } from 'rxjs';
import { STORAGE_LOCATIONS_URL } from '../constants/api.constants';
import { PaginatedResponse } from '../interfaces/paginated-response';
import {
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_TIME,
  toPaginationParams,
} from './pagination';
import { propagateHttpErrors } from '../interceptors/http-error.interceptor';

type StorageLocationsState = {
  loading: boolean;
  search: string;
  pageIndex: number;
  pageSize: number;
  total: number;
};

const initialState: StorageLocationsState = {
  loading: false,
  search: '',
  pageIndex: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  total: 0,
};

/**
 * Lagerorte für die Lagerortübersicht. Der Store liegt in root, damit Suche und Seite erhalten bleiben,
 * wenn man aus der Artikelliste eines Lagerorts zurück navigiert.
 */
export const StorageLocationsStore = signalStore(
  { providedIn: 'root' },
  withState<StorageLocationsState>(initialState),
  withEntities<StorageLocation>(),
  withMethods((store, http = inject(HttpClient)) => {
    // switchMap verwirft veraltete Antworten, wenn sich Suche oder Seite schnell hintereinander ändern
    const load = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true })),
        switchMap(() =>
          http
            .get<PaginatedResponse<StorageLocation>>(STORAGE_LOCATIONS_URL, {
              params: toPaginationParams(store, new HttpParams()),
              context: propagateHttpErrors(),
            })
            .pipe(
              tapResponse({
                next: (response) =>
                  patchState(store, setAllEntities(response.items), {
                    total: response.meta.totalItems,
                    loading: false,
                  }),
                // Fehler meldet der HttpErrorInterceptor; propagateHttpErrors, damit loading zurückgesetzt wird
                error: () => patchState(store, { loading: false }),
              }),
            ),
        ),
      ),
    );

    // Nicht "search" nennen: Methoden überschreiben im SignalStore gleichnamige State-Signale
    const setSearch = rxMethod<string>(
      pipe(
        debounceTime(SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        tap((search) => {
          patchState(store, { search, pageIndex: 0 });
          load();
        }),
      ),
    );

    return {
      load() {
        load();
      },
      setSearch,
      setPage(pageIndex: number, pageSize: number) {
        patchState(store, { pageIndex, pageSize });
        load();
      },
    };
  }),
);
